import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { code: 'fr', label: 'FR', nameKey: 'name', descKey: 'description' },
  { code: 'nl', label: 'NL', nameKey: 'name_nl', descKey: 'description_nl' },
  { code: 'en', label: 'EN', nameKey: 'name_en', descKey: 'description_en' },
  { code: 'es', label: 'ES', nameKey: 'name_es', descKey: 'description_es' },
];

const AdminTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeLang, setActiveLang] = useState({});

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/tickets`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const handleUpdate = async (ticketId, updates) => {
    const auth = localStorage.getItem('admin_auth');
    const ticket = tickets.find(t => t.id === ticketId);
    const updatedTicket = { ...ticket, ...updates };
    
    try {
      await axios.put(`${BACKEND_URL}/api/admin/tickets/${ticketId}`, updatedTicket, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setTickets(tickets.map(t => t.id === ticketId ? updatedTicket : t));
      toast.success('Ticket mis \u00e0 jour!');
    } catch (error) {
      toast.error('Erreur lors de la mise \u00e0 jour');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sold_out': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'available': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'coming_soon': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-white/5 text-[#B9B2A8] border-white/10';
    }
  };

  return (
    <div data-testid="admin-tickets-page">
      <div className="mb-8">
        <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Tickets</h1>
        <p className="text-sm text-[#B9B2A8]">Gérez les vagues de tickets, statuts et traductions (FR/NL/EN/ES)</p>
      </div>

      <div className="space-y-4">
        {tickets.map((ticket, index) => {
          const lang = activeLang[ticket.id] || 'fr';
          const langConf = LANGS.find(l => l.code === lang);
          return (
          <motion.div
            key={ticket.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`bg-white/5 border rounded-xl p-5 ${getStatusColor(ticket.status)}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center mb-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-1">Prix (€)</label>
                <Input
                  type="number"
                  value={ticket.price}
                  onChange={(e) => {
                    const updated = tickets.map(t => t.id === ticket.id ? { ...t, price: parseInt(e.target.value) } : t);
                    setTickets(updated);
                  }}
                  onBlur={() => handleUpdate(ticket.id, { price: ticket.price })}
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-1">Statut</label>
                <Select
                  value={ticket.status}
                  onValueChange={(v) => handleUpdate(ticket.id, { status: v })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B0B0C] border-white/10">
                    <SelectItem value="sold_out" className="text-red-400">Sold Out</SelectItem>
                    <SelectItem value="available" className="text-green-400">Disponible</SelectItem>
                    <SelectItem value="coming_soon" className="text-yellow-400">Bientôt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2 mb-3">
              {LANGS.map(l => (
                <button
                  key={l.code}
                  onClick={() => setActiveLang({ ...activeLang, [ticket.id]: l.code })}
                  className={`px-3 py-1 text-xs font-display uppercase tracking-wider rounded ${
                    lang === l.code
                      ? 'bg-[#C9A84C] text-[#060606]'
                      : 'bg-white/5 text-[#B9B2A8] hover:bg-white/10'
                  }`}
                >
                  {l.label}
                </button>
              ))}
              {lang !== 'fr' && !ticket[langConf.nameKey] && (
                <span className="text-xs text-yellow-400 self-center ml-2">⚠ Vide — fallback FR</span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-1">
                  Nom ({langConf.label})
                </label>
                <Input
                  value={ticket[langConf.nameKey] || ''}
                  placeholder={lang !== 'fr' ? `ex: ${ticket.name}` : ''}
                  onChange={(e) => {
                    const updated = tickets.map(t => t.id === ticket.id ? { ...t, [langConf.nameKey]: e.target.value } : t);
                    setTickets(updated);
                  }}
                  onBlur={() => handleUpdate(ticket.id, { [langConf.nameKey]: ticket[langConf.nameKey] })}
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-1">
                  Description ({langConf.label})
                </label>
                <Input
                  value={ticket[langConf.descKey] || ''}
                  placeholder={lang !== 'fr' ? `ex: ${ticket.description}` : ''}
                  onChange={(e) => {
                    const updated = tickets.map(t => t.id === ticket.id ? { ...t, [langConf.descKey]: e.target.value } : t);
                    setTickets(updated);
                  }}
                  onBlur={() => handleUpdate(ticket.id, { [langConf.descKey]: ticket[langConf.descKey] })}
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
            </div>
          </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminTicketsPage;
